import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faCircleXmark, faFilePdf, faUpload, faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { Formik, Form, Field } from 'formik'
import { DateTime } from 'luxon'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Filters { stripe: string; startDate: string; endDate: string }

const INIT_FILTERS: Filters = { stripe: '', startDate: '2022-01-01', endDate: '2099-12-31' }

const Marketings: React.FC = () => {
  const { getHeaders }  = useContext(AuthContext) as AuthCtx
  const { t, i18n }     = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const btnInvoices = useRef<HTMLButtonElement>(null)

  const [data, setData]           = useState<any[]>([])
  const [loading, setLoading]     = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]     = useState(10)
  const [filters, setFilters]     = useState<Filters>(INIT_FILTERS)
  const [invoices, setInvoices]   = useState<any>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  const [startDatePicker, setStartDatePicker] = useState<Date>(new Date('2023-01-01'))
  const [endDatePicker, setEndDatePicker]     = useState<Date>(new Date())

  const fetchPayments = async (page: number, perPageVal = perPage, f = filters) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: any[]; total: number }>(
        `${url}payments/marketing?page=${page}&per_page=${perPageVal}&stripe=${f.stripe}&start_date=${f.startDate}&end_date=${f.endDate}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchPayments(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchPayments(page, newPerPage)
  }

  const handleSearch = (values: { stripe: string }) => {
    const sd = DateTime.fromJSDate(startDatePicker).toFormat('yyyy-MM-dd')
    const ed = DateTime.fromJSDate(endDatePicker).toFormat('yyyy-MM-dd')
    const newFilters: Filters = { stripe: values.stripe, startDate: sd, endDate: ed }
    setFilters(newFilters)
    fetchPayments(1, perPage, newFilters)
  }

  const cancelSubscription = async (params: string) => {
    const result = await Swal.fire({
      title: t('payments.marketing.remove'),
      text: t('payments.marketing.remove_txt'),
      showCancelButton: true,
      confirmButtonText: t('payments.marketing.unsubcribe'),
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}marketings/unsubscribe`, { subscription: params }, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
      fetchPayments(1)
      setResetPaginationToggle(p => !p)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getInvoices = (pi: any) => {
    setInvoices(pi)
    btnInvoices.current?.click()
  }

  const getInvoiceMarketings = async (params: string) => {
    if (!invoices) return
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}marketings/get-invoices-sub-pdf`, { pi: params, markpaymentId: invoices.id }, { headers })
      const link    = document.createElement('a')
      link.href     = `data:application/pdf;base64,${res.data.message}`
      link.download = res.data.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  useEffect(() => { fetchPayments(1) }, [i18n.language])

  const columns = [
    {
      maxWidth: '25%',
      name: t('payments.marketing.stripe_data'),
      cell: (row: any) => (
        <ul className="ps-0 my-2">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>Checkout ID: </b>{row.cs_stripe_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>Payment intent ID: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.marketing.status'),
      cell: (row: any) => (
        <>
          <div className={`badge ${row.status_suscription === 'active' ? 'text-bg-primary' : 'text-bg-warning'}`}>
            <p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{row.status_suscription}</p>
          </div>
          <p className="mt-1 mb-0 small">{t('payments.marketing.count')}: <b>{row.count_subscription}</b></p>
        </>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.marketing.amount_total'),
      cell: (row: any) => (
        <span>{row.total_amount !== null ? `$${parseFloat(row.total_amount).toFixed(2)}` : '—'}</span>
      ),
    },
    {
      maxWidth: '15%',
      name: t('payments.marketing.due_date'),
      cell: (row: any) => (
        <div className="small">
          <span><b>{t('payments.marketing.created_at')}:</b> {DateTime.fromISO(row.createdAt).toFormat('yyyy-MM-dd')}</span>
          <br />
          <span><b>{t('payments.marketing.expired_at')}:</b> {DateTime.fromISO(row.expireAt).toFormat('yyyy-MM-dd')}</span>
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.marketing.region'),
      cell: (row: any) => (
        <div className="text-start w-100 small">
          <span><b>{t('payments.marketing.country')}:</b> {row?.Markprice?.country}</span>
          <p className="mb-0"><b>{t('payments.marketing.states')}:</b></p>
          {row?.Markprice?.States?.map((e: any, i: number) => <p key={i} className="m-0">{e.name}</p>)}
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.marketing.actions'),
      cell: (row: any) => row.pi_stripe_id === null ? null : (
        <div className="dropdown mb-2">
          <button className="btn btn-sm btn-bd-primary dropdown-toggle" data-bs-toggle="dropdown" style={{ width: 120 }}>
            {t('payments.marketing.options')}
          </button>
          <ul className="dropdown-menu">
            <li>
              <button className="dropdown-item fw-bold" onClick={() => getInvoices(row)}>
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />Invoices
              </button>
            </li>
            <li>
              <Link className="dropdown-item fw-bold" to={`/marketing/advertising/${row.pi_stripe_id}`}>
                <FontAwesomeIcon icon={faUpload} className="me-2" />{t('payments.marketing.upload_banners')}
              </Link>
            </li>
            <li>
              <button
                className={row.status_suscription !== 'active' ? 'dropdown-item disabled text-muted' : 'dropdown-item text-danger fw-bold'}
                onClick={() => cancelSubscription(row.pi_stripe_id)}
              >
                <FontAwesomeIcon icon={faCircleXmark} className="me-2" />{t('payments.marketing.cancel_recurring')}
              </button>
            </li>
          </ul>
        </div>
      ),
    },
  ]

  return (
    <>
      {/* Invoices modal trigger */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#mkt-invoices" ref={btnInvoices} />
      <div className="modal fade" id="mkt-invoices" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('payments.marketing.download_invoices')}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              <ul className="list-group list-group-flush">
                {invoices?.invoices?.map((e: string, i: number) => (
                  <li key={i} className="list-group-item">
                    <button className="btn btn-link p-0 text-decoration-none" onClick={() => getInvoiceMarketings(e)}>{e}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-start">
        <div className="col-sm-4 col-md-2 mb-3 my-1">
          <Link className="btn btn-bd-primary btn-shadow" to="/marketing/advertising/create">
            <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
            <span className="fw-bold">{t('payments.marketing.add_advertising')}</span>
          </Link>
        </div>

        <div className="col-sm-8 col-md-10 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(p => !p)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', background: searchOpen ? '#f8fafc' : '#fff', border: 'none', borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                <Formik initialValues={{ stripe: filters.stripe }} onSubmit={handleSearch} enableReinitialize>
                  <Form>
                    <div className="row align-items-center mb-3">
                      <label className="col-sm-2 col-form-label col-form-label-sm">{t('payments.marketing.search.stripe_payment')}</label>
                      <div className="col-md-10">
                        <Field type="text" className="form-control form-control-sm" name="stripe" placeholder={t('payments.marketing.search.stripe_payment')} />
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-6">
                        <label className="col-form-label col-form-label-sm fw-bold">{t('payments.marketing.search.start_due_date')}</label>
                        <DatePicker
                          dateFormat="yyyy-MM-dd"
                          selected={startDatePicker}
                          onChange={(v: Date) => setStartDatePicker(v)}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="col-form-label col-form-label-sm fw-bold">{t('payments.marketing.search.end_due_date')}</label>
                        <DatePicker
                          dateFormat="yyyy-MM-dd"
                          selected={endDatePicker}
                          onChange={(v: Date) => setEndDatePicker(v)}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <button className="btn btn-sm btn-shadow btn-bd-primary" type="submit">
                      <span className="fw-bold">{t('utilities.search_btn')}</span>
                    </button>
                  </Form>
                </Formik>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12 mb-5">
          <div className="card card-shadow">
            <DataTable
              columns={columns}
              data={data}
              fixedHeader
              fixedHeaderScrollHeight="500px"
              progressPending={loading}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              progressComponent={<CustomLoader />}
              dense
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Marketings
